/**
 * Rules System Index
 * Central export for all route and coverage rules
 * Requirements: 6.1, 6.2, 6.4, 7.1, 7.2, 7.3, 7.5
 */

import { CoverageDefinition, FormationType, Position, CoverageReaction, DBLBReaction, FSReaction, CB2Reaction, DBLB2Reaction, SSReaction, LBReaction } from '../types';
import { cover4MatchDefinition, getCover4MatchStartPosition, getCover4MatchDBLB1StartPosition, getCover4MatchFSStartPosition } from './cover4MatchRules';
import { cover3MatchDefinition } from './cover3MatchRules';
import { manDefinition, manCB2Reactions } from './manRules';
import { cover2Definition } from './cover2Rules';
import { cover4PalmsDefinition } from './cover4PalmsRules';
import { cover6Definition } from './cover6Rules';
import { cover3Definition } from './cover3Rules';

// Re-export route rules
export * from './routeRules';

// Re-export coverage rules
export * from './cover3MatchRules';
export * from './cover3Rules';
export * from './cover4MatchRules';
export * from './manRules';
export * from './cover2Rules';
export * from './cover4PalmsRules';
export * from './cover6Rules';
export * from './kinematics';
export * from './observables';
export * from './responsibility';
export * from './specs/manMatchSpec';
export * from './specs/underneathMatchSpec';
export * from './specs/manTrailSpec';
export * from './specs/palmsSpecs';
export * from './specs/apexMatchSpec';
export * from './specs/hookReadSpec';
export * from './reactionEngine';
export * from './readHarness';
export * from './positionAssignments';
export * from './customDefenseResolver';

// ============================================
// Formation-aware coverage registry
// ============================================

/**
 * Doubles (2x2) coverage definitions — the original set.
 */
const doublesCoverageDefinitions: Record<string, CoverageDefinition> = {
  man: manDefinition,
  cover2: cover2Definition,
  cover3: cover3Definition,
  cover4Match: cover4MatchDefinition,
  cover4Palms: cover4PalmsDefinition,
  cover6: cover6Definition,
  cover3Match: cover3MatchDefinition,
};

/**
 * Trips (3x1) coverage definitions.
 * Currently mirrors doubles except where trips-specific adjustments are needed.
 */

/** Trips man coverage: CB1 lines up directly across from WR1, DB/LB2 across from WR3 on strong side */
const tripsManDefinition: CoverageDefinition = {
  ...manDefinition,
  initialPosition: { x: 20, y: 75 },
  dblb2InitialPosition: { x: 40, y: 75 },
  dblb2ReactionRules: [
    {
      // Slant: Follow WR3 on slant (left side, offset +20)
      routeTrigger: 'slant',
      response: [
        { x: 40, y: 65 },
        { x: 95, y: 43 },
      ],
    },
    {
      // Out: Short 5-yard out toward left sideline
      routeTrigger: 'out',
      response: [
        { x: 40, y: 73 },
        { x: 25, y: 73 },
      ],
    },
    {
      // Deep Out: Backpedal then break to left sideline
      routeTrigger: 'deepOut',
      response: [
        { x: 40, y: 50 },
        { x: 25, y: 50 },
      ],
    },
    {
      // Deep In: Backpedal then break inside
      routeTrigger: 'deepIn',
      response: [
        { x: 40, y: 50 },
        { x: 80, y: 50 },
      ],
    },
    {
      // Comeback: Run deep with WR3, close on comeback
      routeTrigger: 'comeback',
      response: [
        { x: 40, y: 32 },
        { x: 35, y: 45 },
      ],
    },
    {
      // Go: Run stride for stride with WR3
      routeTrigger: 'go',
      response: [
        { x: 40, y: 10 },
      ],
    },
    {
      // Post: Follow WR3 on post
      routeTrigger: 'post',
      response: [
        { x: 40, y: 52 },
        { x: 70, y: 18 },
      ],
    },
    {
      // Corner: Follow WR3 on corner toward left sideline
      routeTrigger: 'corner',
      response: [
        { x: 40, y: 52 },
        { x: 25, y: 18 },
      ],
    },
    {
      // Drag: Shallow cross - trail WR3 across to right
      routeTrigger: 'drag',
      response: [
        { x: 40, y: 78 },
        { x: 90, y: 80 },
      ],
    },
    {
      // Hitch: 5-yard stop - close on WR3
      routeTrigger: 'hitch',
      response: [
        { x: 40, y: 73 },
        { x: 42, y: 76 },
      ],
    },
    {
      // Curl: 10-yard stop - close on WR3
      routeTrigger: 'curl',
      response: [
        { x: 40, y: 52 },
        { x: 42, y: 58 },
      ],
    },
  ],
};

// Trips (3x1) Cover 4 Match (route-based, legacy) archived to ./archive/cover4MatchTripsRules.ts

/**
 * Trips Cover 4 Palms definition (3x1 rules)
 *
 * Position mapping:
 *   Strong Corner = CB1 → Palms on WR1 (#1 strong), takes WR2 on "Out" call
 *   Strong Apex   = DB/LB1 → Wall-Read on WR2 (#2 strong)
 *   Strong Safety = FS → Robber-Read (takes #1 if #2 out early, else robber)
 *   Hook          = LB → Hammer on #3 (RB), "Push" call if RB out fast
 *   Weak Apex     = DB/LB2 → RB or first crosser
 *   Weak Safety   = SS → Poach
 *   Weak Corner   = CB2 → Man on WR4 (#1 weak)
 *
 * WR positions in trips:
 *   WR1 x=20 y=85, WR2 x=30 y=90, WR3 x=40 y=90, WR4 x=80 y=85, RB x=55 y=92
 */

// --- CB1 (Strong Corner): Play Palms on #1 ---
// Key difference from Match: CB1 takes WR2 if WR2 runs short out (within 5 yards)
const tripsCover4PalmsCB1Reactions: CoverageReaction[] = [
  // WR2 short out → "Out" call: CB1 takes WR2
  { routeTrigger: 'out_wr2', wrPositionTrigger: { x: 30, y: 80 }, cbResponse: [{ x: 22, y: 72 }, { x: 20, y: 70 }, { x: 15, y: 70 }], responseDelay: 0 },
  // WR1 slant (underneath) → "Under" call: zone drop to deep quarter
  { routeTrigger: 'slant', wrPositionTrigger: { x: 20, y: 75 }, cbResponse: [{ x: 20, y: 60 }, { x: 20, y: 52 }, { x: 20, y: 44 }, { x: 20, y: 35 }], responseDelay: 0 },
  // WR1 drag (underneath) → "Under" call: zone drop to deep quarter
  { routeTrigger: 'drag', wrPositionTrigger: { x: 20, y: 80 }, cbResponse: [{ x: 20, y: 60 }, { x: 20, y: 52 }, { x: 20, y: 44 }, { x: 20, y: 35 }], responseDelay: 0 },
  // WR1 hitch → "Smash" call: zone drop to deep quarter, eyes on WR2 corner/7
  { routeTrigger: 'hitch', wrPositionTrigger: { x: 20, y: 70 }, cbResponse: [{ x: 20, y: 60 }, { x: 20, y: 52 }, { x: 20, y: 44 }, { x: 20, y: 35 }], responseDelay: 0 },
  // WR1 out (short) → let him go, zone drop to deep quarter (same as hitch/under)
  { routeTrigger: 'out', wrPositionTrigger: { x: 20, y: 70 }, cbResponse: [{ x: 20, y: 60 }, { x: 20, y: 52 }, { x: 20, y: 44 }, { x: 20, y: 35 }], responseDelay: 0 },
  // WR1 deep routes → man on WR1
  { routeTrigger: 'deepOut', wrPositionTrigger: { x: 20, y: 45 }, cbResponse: [{ x: 22, y: 42 }, { x: 7, y: 42 }], responseDelay: 0 },
  { routeTrigger: 'deepIn', wrPositionTrigger: { x: 20, y: 45 }, cbResponse: [{ x: 22, y: 42 }, { x: 58, y: 42 }], responseDelay: 0 },
  { routeTrigger: 'comeback', wrPositionTrigger: { x: 20, y: 50 }, cbResponse: [{ x: 20, y: 30 }, { x: 17, y: 42 }], responseDelay: 0 },
  { routeTrigger: 'go', wrPositionTrigger: { x: 20, y: 70 }, cbResponse: [{ x: 20, y: 40 }, { x: 20, y: 8 }], responseDelay: 0 },
  { routeTrigger: 'post', wrPositionTrigger: { x: 20, y: 55 }, cbResponse: [{ x: 22, y: 50 }, { x: 48, y: 18 }], responseDelay: 0 },
  { routeTrigger: 'corner', wrPositionTrigger: { x: 20, y: 55 }, cbResponse: [{ x: 22, y: 50 }, { x: 7, y: 18 }], responseDelay: 0 },
  { routeTrigger: 'curl', wrPositionTrigger: { x: 20, y: 55 }, cbResponse: [{ x: 22, y: 50 }, { x: 24, y: 58 }], responseDelay: 0 },
];

// --- DB/LB1 (Strong Apex): Play Wall-Read on #2 ---
// Key differences from Match:
// - On "Out" call (WR2 short out): covers flat, reads QB
// - On WR2 vertical: walls from inside, reroutes at top
const tripsCover4PalmsDBLB1Reactions: DBLBReaction[] = [
  // "Out" call (WR2 short out): CB1 takes WR2, Apex covers anyone in flat
  { routeTrigger: 'out_wr2', response: [{ x: 30, y: 65 }, { x: 28, y: 60 }, { x: 25, y: 55 }] },
  // WR1 short in, no one in flat: reroute WR2, cover WR1 crossing
  { routeTrigger: 'slant', response: [{ x: 32, y: 68 }, { x: 45, y: 60 }, { x: 60, y: 48 }] },
  // WR1 short in, no one in flat: reroute WR2, cover WR1 crossing
  { routeTrigger: 'drag', response: [{ x: 32, y: 68 }, { x: 45, y: 72 }, { x: 65, y: 75 }] },
  // WR1 hitch: break to cover WR1
  { routeTrigger: 'hitch', response: [{ x: 28, y: 65 }, { x: 24, y: 70 }, { x: 22, y: 73 }] },
  { routeTrigger: 'hitch_wr1', response: [{ x: 28, y: 65 }, { x: 24, y: 68 }, { x: 22, y: 73 }] },
  // WR1 out: follow to flat
  { routeTrigger: 'out', response: [{ x: 30, y: 65 }, { x: 25, y: 70 }, { x: 10, y: 70 }] },
  { routeTrigger: 'out_wr1', response: [{ x: 28, y: 65 }, { x: 22, y: 68 }, { x: 8, y: 68 }] },
  // WR2 vertical routes: wall from inside then release to flat coverage (FS takes WR2 vertical)
  { routeTrigger: 'go', response: [{ x: 32, y: 62 }, { x: 32, y: 55 }, { x: 30, y: 60 }] },
  { routeTrigger: 'deepOut', response: [{ x: 32, y: 62 }, { x: 32, y: 55 }, { x: 28, y: 60 }] },
  { routeTrigger: 'deepIn', response: [{ x: 32, y: 62 }, { x: 32, y: 55 }, { x: 30, y: 60 }] },
  { routeTrigger: 'comeback', response: [{ x: 32, y: 62 }, { x: 32, y: 55 }, { x: 28, y: 60 }] },
  { routeTrigger: 'post', response: [{ x: 32, y: 62 }, { x: 32, y: 55 }, { x: 30, y: 60 }] },
  { routeTrigger: 'corner', response: [{ x: 32, y: 62 }, { x: 32, y: 55 }, { x: 28, y: 60 }] },
  { routeTrigger: 'curl', response: [{ x: 32, y: 62 }, { x: 32, y: 52 }, { x: 32, y: 60 }] },
  // Push call: RB out fast — break to cover RB in flat
  { routeTrigger: 'out_rb', response: [{ x: 30, y: 65 }, { x: 40, y: 72 }, { x: 55, y: 77 }] },
  { routeTrigger: 'swing_rb', response: [{ x: 30, y: 65 }, { x: 45, y: 75 }, { x: 70, y: 80 }] },
  { routeTrigger: 'wheel_rb', response: [{ x: 30, y: 65 }, { x: 45, y: 75 }, { x: 65, y: 78 }, { x: 65, y: 12 }] },
  // WR3 short out on strong side
  { routeTrigger: 'out_wr3', response: [{ x: 32, y: 65 }, { x: 38, y: 70 }, { x: 30, y: 73 }, { x: 25, y: 75 }] },
];

// --- FS (Strong Safety): Play Robber-Read ---
// Key difference from Match: If WR2 is out early (within 5 yards), FS takes WR1 man
const tripsCover4PalmsFSReactions: FSReaction[] = [
  // WR2 short out ("Out" call): FS takes WR1 man-to-man
  // bracket_* reactions: FS tracks WR1 route from over the top when playing robber
  { routeTrigger: 'bracket_slant', response: [{ x: 28, y: 38 }, { x: 35, y: 30 }, { x: 50, y: 22 }, { x: 65, y: 18 }] },
  { routeTrigger: 'bracket_out', response: [{ x: 28, y: 38 }, { x: 22, y: 32 }, { x: 15, y: 28 }, { x: 10, y: 25 }] },
  { routeTrigger: 'bracket_deepOut', response: [{ x: 28, y: 35 }, { x: 22, y: 25 }, { x: 12, y: 20 }, { x: 8, y: 18 }] },
  { routeTrigger: 'bracket_deepIn', response: [{ x: 28, y: 35 }, { x: 25, y: 25 }, { x: 40, y: 20 }, { x: 55, y: 18 }] },
  { routeTrigger: 'bracket_comeback', response: [{ x: 28, y: 35 }, { x: 22, y: 20 }, { x: 18, y: 12 }, { x: 17, y: 18 }] },
  { routeTrigger: 'bracket_go', response: [{ x: 25, y: 35 }, { x: 22, y: 22 }, { x: 22, y: 12 }, { x: 22, y: 5 }] },
  { routeTrigger: 'bracket_post', response: [{ x: 28, y: 35 }, { x: 25, y: 28 }, { x: 35, y: 18 }, { x: 48, y: 10 }] },
  { routeTrigger: 'bracket_corner', response: [{ x: 28, y: 35 }, { x: 22, y: 28 }, { x: 12, y: 18 }, { x: 8, y: 10 }] },
  { routeTrigger: 'bracket_drag', response: [{ x: 30, y: 40 }, { x: 30, y: 35 }, { x: 40, y: 32 }, { x: 55, y: 30 }] },
  { routeTrigger: 'bracket_hitch', response: [{ x: 28, y: 38 }, { x: 24, y: 32 }, { x: 22, y: 28 }, { x: 22, y: 25 }] },
  { routeTrigger: 'bracket_curl', response: [{ x: 28, y: 35 }, { x: 24, y: 28 }, { x: 22, y: 22 }, { x: 24, y: 25 }] },
  // WR2 out early: FS takes WR1 man — follow WR1's route path
  { routeTrigger: 'out_wr2_slant', response: [{ x: 28, y: 38 }, { x: 30, y: 30 }, { x: 50, y: 22 }, { x: 65, y: 18 }] },
  { routeTrigger: 'out_wr2_go', response: [{ x: 25, y: 35 }, { x: 22, y: 22 }, { x: 22, y: 12 }, { x: 22, y: 5 }] },
  { routeTrigger: 'out_wr2_post', response: [{ x: 28, y: 35 }, { x: 25, y: 28 }, { x: 35, y: 18 }, { x: 48, y: 10 }] },
  { routeTrigger: 'out_wr2_corner', response: [{ x: 28, y: 35 }, { x: 22, y: 28 }, { x: 12, y: 18 }, { x: 8, y: 10 }] },
  // Default short route fallbacks
  { routeTrigger: 'slant', response: [{ x: 30, y: 38 }, { x: 28, y: 30 }, { x: 30, y: 22 }, { x: 35, y: 18 }] },
  { routeTrigger: 'out', response: [{ x: 30, y: 38 }, { x: 25, y: 30 }, { x: 20, y: 22 }, { x: 18, y: 18 }] },
  // WR2 vertical: take WR2 man
  { routeTrigger: 'deepOut', response: [{ x: 35, y: 35 }, { x: 32, y: 42 }, { x: 20, y: 45 }] },
  { routeTrigger: 'deepIn', response: [{ x: 35, y: 35 }, { x: 35, y: 42 }, { x: 58, y: 42 }] },
  { routeTrigger: 'comeback', response: [{ x: 35, y: 35 }, { x: 32, y: 28 }, { x: 28, y: 40 }] },
  { routeTrigger: 'go', response: [{ x: 35, y: 35 }, { x: 35, y: 20 }, { x: 35, y: 10 }] },
  { routeTrigger: 'post', response: [{ x: 35, y: 38 }, { x: 38, y: 30 }, { x: 52, y: 15 }] },
  { routeTrigger: 'corner', response: [{ x: 35, y: 38 }, { x: 32, y: 30 }, { x: 15, y: 15 }] },
  // Default short route fallbacks
  { routeTrigger: 'drag', response: [{ x: 30, y: 38 }, { x: 28, y: 30 }, { x: 25, y: 22 }, { x: 22, y: 18 }] },
  { routeTrigger: 'hitch', response: [{ x: 30, y: 38 }, { x: 25, y: 30 }, { x: 22, y: 22 }, { x: 20, y: 18 }] },
  { routeTrigger: 'curl', response: [{ x: 35, y: 38 }, { x: 33, y: 30 }, { x: 32, y: 22 }, { x: 32, y: 18 }] },
];

// --- DB/LB2 (Weak Apex): RB or first crosser ---
const tripsCover4PalmsDBLB2Reactions: DBLB2Reaction[] = [
  // Default: zone drop, read for crossers
  { routeTrigger: 'slant', response: [{ x: 70, y: 65 }, { x: 65, y: 55 }, { x: 58, y: 48 }] },
  { routeTrigger: 'out', response: [{ x: 70, y: 65 }, { x: 68, y: 55 }, { x: 65, y: 48 }] },
  { routeTrigger: 'deepOut', response: [{ x: 70, y: 65 }, { x: 68, y: 55 }, { x: 65, y: 48 }] },
  { routeTrigger: 'deepIn', response: [{ x: 70, y: 65 }, { x: 65, y: 55 }, { x: 58, y: 48 }] },
  { routeTrigger: 'comeback', response: [{ x: 70, y: 65 }, { x: 68, y: 55 }, { x: 65, y: 48 }] },
  { routeTrigger: 'go', response: [{ x: 70, y: 65 }, { x: 68, y: 55 }, { x: 65, y: 48 }] },
  { routeTrigger: 'post', response: [{ x: 70, y: 65 }, { x: 65, y: 55 }, { x: 58, y: 48 }] },
  { routeTrigger: 'corner', response: [{ x: 70, y: 65 }, { x: 68, y: 55 }, { x: 65, y: 48 }] },
  { routeTrigger: 'drag', response: [{ x: 70, y: 65 }, { x: 65, y: 55 }, { x: 58, y: 48 }] },
  { routeTrigger: 'hitch', response: [{ x: 70, y: 65 }, { x: 68, y: 55 }, { x: 65, y: 48 }] },
  { routeTrigger: 'curl', response: [{ x: 70, y: 65 }, { x: 68, y: 55 }, { x: 65, y: 48 }] },
  // RB to weak side: man on RB
  { routeTrigger: 'out_rb', response: [{ x: 70, y: 65 }, { x: 72, y: 72 }, { x: 85, y: 77 }] },
  { routeTrigger: 'swing_rb', response: [{ x: 70, y: 65 }, { x: 75, y: 75 }, { x: 90, y: 80 }] },
  { routeTrigger: 'wheel_rb', response: [{ x: 70, y: 65 }, { x: 75, y: 75 }, { x: 85, y: 78 }, { x: 85, y: 12 }] },
  { routeTrigger: 'curl_rb', response: [{ x: 70, y: 65 }, { x: 60, y: 72 }, { x: 55, y: 77 }] },
  { routeTrigger: 'zone_drop', response: [{ x: 70, y: 65 }, { x: 68, y: 55 }, { x: 65, y: 48 }] },
  // Pick up crossers entering weak side
  { routeTrigger: 'slant_wr4', response: [{ x: 70, y: 65 }, { x: 60, y: 55 }, { x: 45, y: 42 }] },
  { routeTrigger: 'slant_wr3', response: [{ x: 70, y: 65 }, { x: 62, y: 55 }, { x: 55, y: 45 }] },
  { routeTrigger: 'out_wr4', response: [{ x: 72, y: 65 }, { x: 78, y: 68 }, { x: 92, y: 68 }] },
  { routeTrigger: 'hitch_wr4', response: [{ x: 72, y: 65 }, { x: 76, y: 68 }, { x: 78, y: 73 }] },
];

// --- SS (Weak Safety): Play Poach ---
const tripsCover4PalmsSSReactions: SSReaction[] = [
  // WR3 not vertical: bracket WR4
  { routeTrigger: 'slant', response: [{ x: 65, y: 35 }, { x: 72, y: 25 }, { x: 78, y: 18 }] },
  { routeTrigger: 'out', response: [{ x: 65, y: 35 }, { x: 72, y: 25 }, { x: 78, y: 18 }] },
  // WR3 vertical: help on WR3
  { routeTrigger: 'deepOut', response: [{ x: 62, y: 42 }, { x: 55, y: 40 }, { x: 48, y: 40 }, { x: 40, y: 42 }, { x: 35, y: 45 }] },
  { routeTrigger: 'deepIn', response: [{ x: 62, y: 40 }, { x: 55, y: 38 }, { x: 50, y: 38 }, { x: 55, y: 40 }, { x: 62, y: 42 }] },
  { routeTrigger: 'comeback', response: [{ x: 60, y: 38 }, { x: 52, y: 30 }, { x: 45, y: 22 }, { x: 42, y: 18 }, { x: 40, y: 28 }] },
  { routeTrigger: 'go', response: [{ x: 60, y: 38 }, { x: 52, y: 28 }, { x: 45, y: 18 }, { x: 42, y: 10 }, { x: 42, y: 5 }] },
  { routeTrigger: 'post', response: [{ x: 60, y: 38 }, { x: 55, y: 30 }, { x: 52, y: 24 }, { x: 56, y: 18 }, { x: 65, y: 14 }] },
  { routeTrigger: 'corner', response: [{ x: 60, y: 38 }, { x: 52, y: 30 }, { x: 45, y: 22 }, { x: 38, y: 15 }, { x: 32, y: 10 }] },
  // WR3 not vertical: bracket WR4
  { routeTrigger: 'drag', response: [{ x: 65, y: 35 }, { x: 72, y: 25 }, { x: 78, y: 18 }] },
  { routeTrigger: 'hitch', response: [{ x: 65, y: 35 }, { x: 72, y: 25 }, { x: 78, y: 18 }] },
  { routeTrigger: 'curl', response: [{ x: 62, y: 40 }, { x: 55, y: 38 }, { x: 50, y: 42 }, { x: 50, y: 48 }] },
];

// --- LB (Hook): Play Hammer on #3 (RB) ---
// "Push" call if RB out fast, then cover WR2 with underneath leverage
const tripsCover4PalmsLBReactions: LBReaction[] = [
  { routeTrigger: 'out', response: [{ x: 52, y: 68 }, { x: 57, y: 77 }, { x: 85, y: 77 }] },
  { routeTrigger: 'swing', response: [{ x: 52, y: 68 }, { x: 70, y: 82 }, { x: 90, y: 78 }] },
  { routeTrigger: 'curl', response: [{ x: 52, y: 68 }, { x: 55, y: 77 }, { x: 54, y: 80 }] },
  { routeTrigger: 'wheel', response: [{ x: 52, y: 68 }, { x: 70, y: 82 }, { x: 85, y: 78 }, { x: 85, y: 12 }] },
  { routeTrigger: 'zone_drop', response: [{ x: 50, y: 66 }, { x: 50, y: 60 }, { x: 50, y: 52 }] },
  // Crosser triggers
  { routeTrigger: 'slant_wr1', response: [{ x: 48, y: 64 }, { x: 42, y: 52 }, { x: 52, y: 44 }, { x: 64, y: 42 }, { x: 75, y: 40 }] },
  { routeTrigger: 'slant_wr2', response: [{ x: 48, y: 64 }, { x: 42, y: 52 }, { x: 55, y: 46 }, { x: 70, y: 44 }, { x: 83, y: 43 }] },
  { routeTrigger: 'slant_wr3', response: [{ x: 50, y: 64 }, { x: 52, y: 55 }, { x: 65, y: 48 }, { x: 80, y: 44 }, { x: 93, y: 43 }] },
  { routeTrigger: 'slant_wr4', response: [{ x: 52, y: 64 }, { x: 58, y: 52 }, { x: 48, y: 44 }, { x: 36, y: 42 }, { x: 25, y: 40 }] },
  { routeTrigger: 'drag_wr1', response: [{ x: 48, y: 68 }, { x: 45, y: 72 }, { x: 55, y: 74 }, { x: 68, y: 75 }] },
  { routeTrigger: 'drag_wr2', response: [{ x: 48, y: 68 }, { x: 45, y: 74 }, { x: 60, y: 78 }, { x: 78, y: 80 }] },
  { routeTrigger: 'drag_wr3', response: [{ x: 50, y: 68 }, { x: 52, y: 74 }, { x: 70, y: 78 }, { x: 88, y: 80 }] },
  { routeTrigger: 'drag_wr4', response: [{ x: 52, y: 68 }, { x: 55, y: 74 }, { x: 40, y: 74 }, { x: 32, y: 75 }] },
  { routeTrigger: 'slant_wr3_handoff', response: [{ x: 50, y: 64 }, { x: 52, y: 55 }, { x: 65, y: 48 }, { x: 80, y: 44 }] },
  { routeTrigger: 'slant_wr4_handoff', response: [{ x: 52, y: 64 }, { x: 58, y: 52 }, { x: 48, y: 44 }, { x: 36, y: 42 }] },
  { routeTrigger: 'drag_wr3_handoff', response: [{ x: 50, y: 68 }, { x: 52, y: 74 }, { x: 70, y: 78 }] },
  { routeTrigger: 'drag_wr4_handoff', response: [{ x: 52, y: 68 }, { x: 55, y: 74 }, { x: 40, y: 74 }] },
];

const tripsCover4PalmsDefinition: CoverageDefinition = {
  id: 'cover4Palms',
  name: 'Cover 4 Palms',
  description: 'Palms coverage vs 3x1 — strong side palms/wall-read/robber-read, weak side poach/man',
  initialPosition: { x: 20, y: 65 },
  reactionRules: tripsCover4PalmsCB1Reactions,
  dblb1InitialPosition: { x: 30, y: 72 },
  dblb1ReactionRules: tripsCover4PalmsDBLB1Reactions,
  fsInitialPosition: { x: 35, y: 50 },
  fsReactionRules: tripsCover4PalmsFSReactions,
  cb2InitialPosition: { x: 80, y: 65 },
  cb2ReactionRules: manCB2Reactions,
  dblb2InitialPosition: { x: 70, y: 72 },
  dblb2ReactionRules: tripsCover4PalmsDBLB2Reactions,
  ssInitialPosition: { x: 65, y: 50 },
  ssReactionRules: tripsCover4PalmsSSReactions,
  lbInitialPosition: { x: 50, y: 70 },
  lbReactionRules: tripsCover4PalmsLBReactions,
};

/**
 * Trips Cover 3 Match definition (3x1 rules)
 *
 * Position mapping:
 *   Strong Corner = CB1 → Off Mod on WR1 (#1 strong)
 *   Strong Apex   = DB/LB1 → Man #2 with #3 priority (automatic "Push")
 *   Strong Hook   = LB → Man #3 with handoff rules
 *   Weak Hook     = SS → Middle hook, "3 Up is 3" rule
 *   Weak Apex     = DB/LB2 → RB or first crosser
 *   Weak Corner   = CB2 → Man on WR4 (#1 weak)
 *   Deep Safety   = FS → Deep middle, reads #2 and #3
 *
 * WR positions in trips:
 *   WR1 x=20 y=85, WR2 x=30 y=90, WR3 x=40 y=90, WR4 x=80 y=85, RB x=55 y=92
 */

// --- CB1 (Strong Corner): Play Off Mod on #1 ---
// Align 7-8 yards off with outside leverage. Man on #1 except any route under 10 yards.
const tripsCover3MatchCB1Reactions: CoverageReaction[] = [
  // WR1 slant (under 10 yds) → zone drop to deep third
  { routeTrigger: 'slant', wrPositionTrigger: { x: 20, y: 75 }, cbResponse: [{ x: 20, y: 58 }, { x: 20, y: 45 }, { x: 20, y: 32 }], responseDelay: 0 },
  // WR1 drag (under 10 yds) → zone drop to deep third
  { routeTrigger: 'drag', wrPositionTrigger: { x: 20, y: 80 }, cbResponse: [{ x: 20, y: 58 }, { x: 20, y: 45 }, { x: 20, y: 32 }], responseDelay: 0 },
  // WR1 hitch (under 10 yds) → zone drop to deep third
  { routeTrigger: 'hitch', wrPositionTrigger: { x: 20, y: 70 }, cbResponse: [{ x: 20, y: 58 }, { x: 20, y: 45 }, { x: 20, y: 32 }], responseDelay: 0 },
  // WR1 out (under 10 yds) → zone drop to deep third
  { routeTrigger: 'out', wrPositionTrigger: { x: 20, y: 70 }, cbResponse: [{ x: 20, y: 58 }, { x: 20, y: 45 }, { x: 20, y: 32 }], responseDelay: 0 },
  // WR1 deep routes → man on WR1
  { routeTrigger: 'deepOut', wrPositionTrigger: { x: 20, y: 45 }, cbResponse: [{ x: 22, y: 42 }, { x: 7, y: 42 }], responseDelay: 0 },
  { routeTrigger: 'deepIn', wrPositionTrigger: { x: 20, y: 45 }, cbResponse: [{ x: 22, y: 42 }, { x: 58, y: 42 }], responseDelay: 0 },
  { routeTrigger: 'comeback', wrPositionTrigger: { x: 20, y: 50 }, cbResponse: [{ x: 20, y: 30 }, { x: 17, y: 42 }], responseDelay: 0 },
  { routeTrigger: 'go', wrPositionTrigger: { x: 20, y: 70 }, cbResponse: [{ x: 20, y: 40 }, { x: 20, y: 8 }], responseDelay: 0 },
  { routeTrigger: 'post', wrPositionTrigger: { x: 20, y: 55 }, cbResponse: [{ x: 22, y: 50 }, { x: 48, y: 18 }], responseDelay: 0 },
  { routeTrigger: 'corner', wrPositionTrigger: { x: 20, y: 55 }, cbResponse: [{ x: 22, y: 50 }, { x: 7, y: 18 }], responseDelay: 0 },
  { routeTrigger: 'curl', wrPositionTrigger: { x: 20, y: 55 }, cbResponse: [{ x: 22, y: 50 }, { x: 24, y: 58 }], responseDelay: 0 },
];

// --- DB/LB1 (Strong Apex): Man #2 with #3 priority ---
// If #3 is out now → take #3 with top-down leverage (automatic "Push")
// If WR1 runs a hitch → cover WR1 on the hitch
// If #2 underneath → "Under" call, buzz flat, rob WR1
const tripsCover3MatchDBLB1Reactions: DBLBReaction[] = [
  // #3 out fast: take RB with top-down leverage
  { routeTrigger: 'out_rb', response: [{ x: 30, y: 65 }, { x: 40, y: 72 }, { x: 55, y: 77 }] },
  { routeTrigger: 'swing_rb', response: [{ x: 30, y: 65 }, { x: 45, y: 75 }, { x: 70, y: 80 }] },
  { routeTrigger: 'wheel_rb', response: [{ x: 30, y: 65 }, { x: 45, y: 75 }, { x: 65, y: 78 }, { x: 65, y: 12 }] },
  // WR1 hitch: break to cover WR1
  { routeTrigger: 'hitch', response: [{ x: 28, y: 65 }, { x: 24, y: 70 }, { x: 22, y: 73 }] },
  { routeTrigger: 'hitch_wr1', response: [{ x: 28, y: 65 }, { x: 24, y: 68 }, { x: 22, y: 73 }] },
  // WR2 underneath (slant) → "Under" call: buzz flat, rob WR1
  { routeTrigger: 'slant', response: [{ x: 30, y: 65 }, { x: 25, y: 68 }, { x: 20, y: 72 }] },
  // WR2 underneath (drag) → "Under" call: buzz flat, rob WR1
  { routeTrigger: 'drag', response: [{ x: 30, y: 65 }, { x: 25, y: 68 }, { x: 20, y: 72 }] },
  // WR2 deep routes: man on WR2
  { routeTrigger: 'out', response: [{ x: 30, y: 65 }, { x: 25, y: 70 }, { x: 10, y: 70 }] },
  { routeTrigger: 'go', response: [{ x: 32, y: 62 }, { x: 32, y: 45 }, { x: 32, y: 20 }] },
  { routeTrigger: 'deepOut', response: [{ x: 32, y: 62 }, { x: 32, y: 48 }, { x: 20, y: 55 }] },
  { routeTrigger: 'deepIn', response: [{ x: 32, y: 62 }, { x: 32, y: 48 }, { x: 58, y: 48 }] },
  { routeTrigger: 'comeback', response: [{ x: 32, y: 62 }, { x: 32, y: 35 }, { x: 28, y: 45 }] },
  { routeTrigger: 'post', response: [{ x: 32, y: 62 }, { x: 35, y: 48 }, { x: 52, y: 20 }] },
  { routeTrigger: 'corner', response: [{ x: 32, y: 62 }, { x: 32, y: 48 }, { x: 15, y: 20 }] },
  { routeTrigger: 'curl', response: [{ x: 32, y: 62 }, { x: 32, y: 52 }, { x: 32, y: 60 }] },
  // WR3 short out on strong side
  { routeTrigger: 'out_wr3', response: [{ x: 32, y: 65 }, { x: 38, y: 70 }, { x: 30, y: 73 }, { x: 25, y: 75 }] },
];

// --- FS (Deep Safety): Play Deep Middle ---
// Reads #2 and #3 releases. Stops Post/Over/Seam by #2 or #3.
// If none, denies vertical in-breaking by #1.
const tripsCover3MatchFSReactions: FSReaction[] = [
  // WR2 vertical → track Post/Seam/Over by WR2
  { routeTrigger: 'go', response: [{ x: 50, y: 42 }, { x: 45, y: 30 }, { x: 40, y: 15 }] },
  { routeTrigger: 'post', response: [{ x: 50, y: 42 }, { x: 48, y: 30 }, { x: 55, y: 18 }] },
  { routeTrigger: 'deepIn', response: [{ x: 50, y: 42 }, { x: 48, y: 35 }, { x: 55, y: 30 }] },
  { routeTrigger: 'deepOut', response: [{ x: 50, y: 42 }, { x: 45, y: 35 }, { x: 35, y: 30 }] },
  { routeTrigger: 'comeback', response: [{ x: 50, y: 42 }, { x: 45, y: 32 }, { x: 40, y: 25 }] },
  { routeTrigger: 'corner', response: [{ x: 50, y: 42 }, { x: 45, y: 32 }, { x: 30, y: 20 }] },
  // WR2/WR3 not vertical → deny vertical in-breaking by WR1
  { routeTrigger: 'slant', response: [{ x: 50, y: 42 }, { x: 45, y: 35 }, { x: 40, y: 28 }] },
  { routeTrigger: 'out', response: [{ x: 50, y: 42 }, { x: 48, y: 35 }, { x: 45, y: 28 }] },
  { routeTrigger: 'drag', response: [{ x: 50, y: 42 }, { x: 48, y: 35 }, { x: 45, y: 28 }] },
  { routeTrigger: 'hitch', response: [{ x: 50, y: 42 }, { x: 48, y: 35 }, { x: 45, y: 28 }] },
  { routeTrigger: 'curl', response: [{ x: 50, y: 42 }, { x: 48, y: 35 }, { x: 45, y: 28 }] },
  // bracket_ triggers for tracking WR1 deep in-breaking from safety depth
  { routeTrigger: 'bracket_post', response: [{ x: 50, y: 42 }, { x: 45, y: 30 }, { x: 48, y: 18 }] },
  { routeTrigger: 'bracket_deepIn', response: [{ x: 50, y: 42 }, { x: 45, y: 30 }, { x: 55, y: 22 }] },
  { routeTrigger: 'bracket_go', response: [{ x: 50, y: 42 }, { x: 40, y: 28 }, { x: 35, y: 15 }] },
];

// --- LB (Strong Hook): Man #3 with handoff rules ---
// If #3 out now → take WR2 man with vertical depth
// If #3 shallow → Curl-Hook area, take weak #1 crosser
// If #3 bends vertical inside (Over/Deep Cross) → release to Weak Hook, play Curl-Hook
const tripsCover3MatchLBReactions: LBReaction[] = [
  // #3 out now → take WR2 man with vertical depth
  { routeTrigger: 'out', response: [{ x: 45, y: 66 }, { x: 38, y: 58 }, { x: 35, y: 45 }] },
  { routeTrigger: 'swing', response: [{ x: 45, y: 66 }, { x: 38, y: 58 }, { x: 35, y: 45 }] },
  // #3 (RB) releases but not out fast → man on RB
  { routeTrigger: 'curl', response: [{ x: 45, y: 68 }, { x: 50, y: 77 }, { x: 52, y: 80 }] },
  { routeTrigger: 'wheel', response: [{ x: 45, y: 68 }, { x: 60, y: 80 }, { x: 75, y: 78 }, { x: 75, y: 12 }] },
  // #3 shallow → play Curl-Hook, take crossers from weak side
  { routeTrigger: 'zone_drop', response: [{ x: 45, y: 64 }, { x: 42, y: 55 }, { x: 40, y: 48 }] },
  // Crosser triggers - take weak #1 (WR4) crossing
  { routeTrigger: 'slant_wr4', response: [{ x: 45, y: 64 }, { x: 50, y: 55 }, { x: 42, y: 48 }, { x: 32, y: 42 }] },
  { routeTrigger: 'drag_wr4', response: [{ x: 45, y: 68 }, { x: 50, y: 72 }, { x: 40, y: 74 }, { x: 32, y: 75 }] },
  // #3 bends vertical inside (Deep Cross/Over) → release to Weak Hook, play Curl-Hook
  { routeTrigger: 'slant_wr3', response: [{ x: 45, y: 64 }, { x: 42, y: 55 }, { x: 40, y: 48 }] },
  { routeTrigger: 'drag_wr3', response: [{ x: 45, y: 64 }, { x: 42, y: 55 }, { x: 40, y: 48 }] },
  // Strong side crosser triggers
  { routeTrigger: 'slant_wr1', response: [{ x: 45, y: 64 }, { x: 42, y: 52 }, { x: 52, y: 44 }, { x: 64, y: 42 }] },
  { routeTrigger: 'slant_wr2', response: [{ x: 45, y: 64 }, { x: 42, y: 52 }, { x: 55, y: 46 }, { x: 70, y: 44 }] },
  { routeTrigger: 'drag_wr1', response: [{ x: 45, y: 68 }, { x: 42, y: 72 }, { x: 55, y: 74 }, { x: 68, y: 75 }] },
  { routeTrigger: 'drag_wr2', response: [{ x: 45, y: 68 }, { x: 42, y: 74 }, { x: 60, y: 78 }, { x: 78, y: 80 }] },
];

// --- SS (Weak Hook): Defend Middle Hook, "3 Up is 3" rule ---
// If #3 bends vertical inside → take #3 man ("3 Up is 3")
// If RB out weak → responsible for shallow crossers from strong side
// If #3 not vertical inside and RB out fast strong → cover RB with top-down leverage
const tripsCover3MatchSSReactions: SSReaction[] = [
  // Default: defend middle hook deep-to-short
  { routeTrigger: 'slant', response: [{ x: 68, y: 62 }, { x: 60, y: 52 }, { x: 55, y: 45 }] },
  { routeTrigger: 'out', response: [{ x: 68, y: 62 }, { x: 65, y: 52 }, { x: 60, y: 45 }] },
  { routeTrigger: 'deepOut', response: [{ x: 68, y: 62 }, { x: 65, y: 52 }, { x: 60, y: 45 }] },
  { routeTrigger: 'deepIn', response: [{ x: 68, y: 62 }, { x: 60, y: 52 }, { x: 55, y: 45 }] },
  { routeTrigger: 'comeback', response: [{ x: 68, y: 62 }, { x: 65, y: 52 }, { x: 60, y: 45 }] },
  { routeTrigger: 'go', response: [{ x: 68, y: 62 }, { x: 65, y: 52 }, { x: 60, y: 45 }] },
  { routeTrigger: 'post', response: [{ x: 68, y: 62 }, { x: 60, y: 52 }, { x: 55, y: 45 }] },
  { routeTrigger: 'corner', response: [{ x: 68, y: 62 }, { x: 65, y: 52 }, { x: 60, y: 45 }] },
  { routeTrigger: 'drag', response: [{ x: 68, y: 62 }, { x: 60, y: 52 }, { x: 55, y: 45 }] },
  { routeTrigger: 'hitch', response: [{ x: 68, y: 62 }, { x: 65, y: 52 }, { x: 60, y: 45 }] },
  { routeTrigger: 'curl', response: [{ x: 68, y: 62 }, { x: 65, y: 52 }, { x: 60, y: 45 }] },
  // "3 Up is 3": WR3 bends vertical inside (Deep Cross/Over) → take WR3 man
  { routeTrigger: 'slant_wr3', response: [{ x: 65, y: 60 }, { x: 58, y: 50 }, { x: 65, y: 42 }, { x: 80, y: 38 }] },
  { routeTrigger: 'drag_wr3', response: [{ x: 65, y: 60 }, { x: 60, y: 55 }, { x: 70, y: 50 }, { x: 85, y: 48 }] },
  // RB out fast strong → cover RB with top-down leverage
  { routeTrigger: 'out_rb', response: [{ x: 68, y: 62 }, { x: 60, y: 68 }, { x: 55, y: 77 }] },
  { routeTrigger: 'swing_rb', response: [{ x: 68, y: 62 }, { x: 62, y: 70 }, { x: 70, y: 80 }] },
  // Take crossers from strong side when RB out weak
  { routeTrigger: 'slant_wr1', response: [{ x: 65, y: 60 }, { x: 58, y: 52 }, { x: 65, y: 44 }, { x: 72, y: 40 }] },
  { routeTrigger: 'slant_wr2', response: [{ x: 65, y: 60 }, { x: 58, y: 52 }, { x: 68, y: 46 }, { x: 78, y: 42 }] },
  { routeTrigger: 'drag_wr1', response: [{ x: 65, y: 62 }, { x: 60, y: 68 }, { x: 68, y: 72 }, { x: 75, y: 75 }] },
  { routeTrigger: 'drag_wr2', response: [{ x: 65, y: 62 }, { x: 60, y: 68 }, { x: 70, y: 74 }, { x: 80, y: 78 }] },
];

// --- DB/LB2 (Weak Apex): RB or first crosser ---
// RB out weak → man on RB
// RB not out weak → take crossers from strong side; if none, rob WR4
const tripsCover3MatchDBLB2Reactions: DBLB2Reaction[] = [
  // Default: zone drop, read for crossers, rob WR4
  { routeTrigger: 'slant', response: [{ x: 55, y: 65 }, { x: 58, y: 55 }, { x: 62, y: 48 }] },
  { routeTrigger: 'out', response: [{ x: 55, y: 65 }, { x: 58, y: 55 }, { x: 62, y: 48 }] },
  { routeTrigger: 'deepOut', response: [{ x: 55, y: 65 }, { x: 58, y: 55 }, { x: 62, y: 48 }] },
  { routeTrigger: 'deepIn', response: [{ x: 55, y: 65 }, { x: 58, y: 55 }, { x: 62, y: 48 }] },
  { routeTrigger: 'comeback', response: [{ x: 55, y: 65 }, { x: 58, y: 55 }, { x: 62, y: 48 }] },
  { routeTrigger: 'go', response: [{ x: 55, y: 65 }, { x: 58, y: 55 }, { x: 62, y: 48 }] },
  { routeTrigger: 'post', response: [{ x: 55, y: 65 }, { x: 58, y: 55 }, { x: 62, y: 48 }] },
  { routeTrigger: 'corner', response: [{ x: 55, y: 65 }, { x: 58, y: 55 }, { x: 62, y: 48 }] },
  { routeTrigger: 'drag', response: [{ x: 55, y: 65 }, { x: 58, y: 55 }, { x: 62, y: 48 }] },
  { routeTrigger: 'hitch', response: [{ x: 55, y: 65 }, { x: 58, y: 55 }, { x: 62, y: 48 }] },
  { routeTrigger: 'curl', response: [{ x: 55, y: 65 }, { x: 58, y: 55 }, { x: 62, y: 48 }] },
  // RB to weak side: man on RB
  { routeTrigger: 'out_rb', response: [{ x: 55, y: 65 }, { x: 62, y: 72 }, { x: 85, y: 77 }] },
  { routeTrigger: 'swing_rb', response: [{ x: 55, y: 65 }, { x: 65, y: 75 }, { x: 90, y: 80 }] },
  { routeTrigger: 'wheel_rb', response: [{ x: 55, y: 65 }, { x: 65, y: 75 }, { x: 85, y: 78 }, { x: 85, y: 12 }] },
  { routeTrigger: 'curl_rb', response: [{ x: 55, y: 65 }, { x: 58, y: 72 }, { x: 55, y: 77 }] },
  { routeTrigger: 'zone_drop', response: [{ x: 55, y: 65 }, { x: 58, y: 55 }, { x: 62, y: 48 }] },
  // Pick up crossers from strong side
  { routeTrigger: 'slant_wr1', response: [{ x: 55, y: 65 }, { x: 58, y: 55 }, { x: 65, y: 45 }, { x: 72, y: 40 }] },
  { routeTrigger: 'slant_wr2', response: [{ x: 55, y: 65 }, { x: 58, y: 55 }, { x: 68, y: 46 }, { x: 80, y: 42 }] },
  { routeTrigger: 'slant_wr4', response: [{ x: 55, y: 65 }, { x: 60, y: 55 }, { x: 45, y: 42 }] },
  { routeTrigger: 'drag_wr1', response: [{ x: 55, y: 65 }, { x: 58, y: 70 }, { x: 68, y: 74 }, { x: 75, y: 75 }] },
  { routeTrigger: 'drag_wr4', response: [{ x: 55, y: 65 }, { x: 62, y: 70 }, { x: 48, y: 74 }, { x: 35, y: 75 }] },
  { routeTrigger: 'out_wr4', response: [{ x: 57, y: 65 }, { x: 68, y: 68 }, { x: 92, y: 68 }] },
  { routeTrigger: 'hitch_wr4', response: [{ x: 57, y: 65 }, { x: 68, y: 68 }, { x: 78, y: 73 }] },
];

const tripsCover3MatchDefinition: CoverageDefinition = {
  id: 'cover3Match',
  name: 'Cover 3 Match',
  description: 'Cover 3 Match vs 3x1 — strong side off-mod/man-#2/man-#3, weak side crosser/poach, deep middle safety',
  initialPosition: { x: 20, y: 65 },
  reactionRules: tripsCover3MatchCB1Reactions,
  dblb1InitialPosition: { x: 30, y: 72 },
  dblb1ReactionRules: tripsCover3MatchDBLB1Reactions,
  fsInitialPosition: { x: 50, y: 45 },
  fsReactionRules: tripsCover3MatchFSReactions,
  cb2InitialPosition: { x: 80, y: 65 },
  cb2ReactionRules: manCB2Reactions,
  // Alignments for the perception-based trips roles: the LB mans the strong-side #3 (WR3 ~x40),
  // DB/LB2 is the middle hook, and SS is the weak-side apex. (Legacy reaction rules below are
  // superseded by the reaction engine but kept for reference.)
  dblb2InitialPosition: { x: 52, y: 72 },
  dblb2ReactionRules: tripsCover3MatchDBLB2Reactions,
  ssInitialPosition: { x: 64, y: 72 },
  ssReactionRules: tripsCover3MatchSSReactions,
  lbInitialPosition: { x: 44, y: 68 },
  lbReactionRules: tripsCover3MatchLBReactions,
};

/**
 * Trips Cover 6 definition (3x1 rules)
 *
 * Cover 6 vs 3x1: 3-WR side plays Cover 4 Palms, single-WR side simplified.
 *
 * Position mapping:
 *   3 WR Side (Cover 4 Palms):
 *     CB1 (Corner) → Palms on WR1 (#1), takes WR2 on "Out" call
 *     DB/LB1 (Apex/Slot) → Wall-Read on WR2 (#2)
 *     FS (Safety) → Robber-Read
 *     LB (Hook) → Hammer on #3 (RB), "Push" call if RB out fast
 *
 *   Single WR Side:
 *     CB2 (Corner) → Man on WR4 everywhere
 *     SS (Safety) → Deep half, no match responsibilities
 *     DB/LB2 (Slot) → Zone, picks up crossers from trips side
 *
 * WR positions in trips:
 *   WR1 x=20 y=85, WR2 x=30 y=90, WR3 x=40 y=90, WR4 x=80 y=85, RB x=55 y=92
 */

// --- CB1 (3 WR side Corner): Play Palms on #1 ---
// Same as trips Cover 4 Palms CB1
const tripsCover6CB1Reactions: CoverageReaction[] = [
  // WR2 short out → "Out" call: CB1 takes WR2
  { routeTrigger: 'out_wr2', wrPositionTrigger: { x: 30, y: 80 }, cbResponse: [{ x: 22, y: 72 }, { x: 20, y: 70 }, { x: 15, y: 70 }], responseDelay: 0 },
  // WR1 slant (underneath) → "Under" call: zone drop to deep quarter
  { routeTrigger: 'slant', wrPositionTrigger: { x: 20, y: 75 }, cbResponse: [{ x: 20, y: 60 }, { x: 20, y: 52 }, { x: 20, y: 44 }, { x: 20, y: 35 }], responseDelay: 0 },
  // WR1 drag (underneath) → "Under" call: zone drop to deep quarter
  { routeTrigger: 'drag', wrPositionTrigger: { x: 20, y: 80 }, cbResponse: [{ x: 20, y: 60 }, { x: 20, y: 52 }, { x: 20, y: 44 }, { x: 20, y: 35 }], responseDelay: 0 },
  // WR1 hitch → "Smash" call: zone drop to deep quarter
  { routeTrigger: 'hitch', wrPositionTrigger: { x: 20, y: 70 }, cbResponse: [{ x: 20, y: 60 }, { x: 20, y: 52 }, { x: 20, y: 44 }, { x: 20, y: 35 }], responseDelay: 0 },
  // WR1 out (short) → let him go, zone drop
  { routeTrigger: 'out', wrPositionTrigger: { x: 20, y: 70 }, cbResponse: [{ x: 20, y: 60 }, { x: 20, y: 52 }, { x: 20, y: 44 }, { x: 20, y: 35 }], responseDelay: 0 },
  // WR1 deep routes → man on WR1
  { routeTrigger: 'deepOut', wrPositionTrigger: { x: 20, y: 45 }, cbResponse: [{ x: 22, y: 42 }, { x: 7, y: 42 }], responseDelay: 0 },
  { routeTrigger: 'deepIn', wrPositionTrigger: { x: 20, y: 45 }, cbResponse: [{ x: 22, y: 42 }, { x: 58, y: 42 }], responseDelay: 0 },
  { routeTrigger: 'comeback', wrPositionTrigger: { x: 20, y: 50 }, cbResponse: [{ x: 20, y: 30 }, { x: 17, y: 42 }], responseDelay: 0 },
  { routeTrigger: 'go', wrPositionTrigger: { x: 20, y: 70 }, cbResponse: [{ x: 20, y: 40 }, { x: 20, y: 8 }], responseDelay: 0 },
  { routeTrigger: 'post', wrPositionTrigger: { x: 20, y: 55 }, cbResponse: [{ x: 22, y: 50 }, { x: 48, y: 18 }], responseDelay: 0 },
  { routeTrigger: 'corner', wrPositionTrigger: { x: 20, y: 55 }, cbResponse: [{ x: 22, y: 50 }, { x: 7, y: 18 }], responseDelay: 0 },
  { routeTrigger: 'curl', wrPositionTrigger: { x: 20, y: 55 }, cbResponse: [{ x: 22, y: 50 }, { x: 24, y: 58 }], responseDelay: 0 },
];

// --- DB/LB1 (3 WR side Apex/Slot): Play Wall-Read on #2 ---
const tripsCover6DBLB1Reactions: DBLBReaction[] = [
  // "Out" call (WR2 short out): covers flat
  { routeTrigger: 'out_wr2', response: [{ x: 30, y: 65 }, { x: 28, y: 60 }, { x: 25, y: 55 }] },
  // WR1 short in, no one in flat: reroute WR2, cover WR1 crossing — closes on WR1's slant ending at (75, 40)
  { routeTrigger: 'slant', response: [{ x: 32, y: 68 }, { x: 38, y: 60 }, { x: 50, y: 50 }, { x: 65, y: 43 }, { x: 75, y: 40 }] },
  // WR1 short in, no one in flat: reroute WR2, cover WR1 crossing — closes on WR1's drag ending at (70, 75)
  { routeTrigger: 'drag', response: [{ x: 32, y: 70 }, { x: 38, y: 73 }, { x: 50, y: 74 }, { x: 65, y: 75 }, { x: 70, y: 75 }] },
  // WR1 hitch: break to cover WR1
  { routeTrigger: 'hitch', response: [{ x: 28, y: 65 }, { x: 24, y: 70 }, { x: 22, y: 73 }] },
  { routeTrigger: 'hitch_wr1', response: [{ x: 28, y: 65 }, { x: 24, y: 68 }, { x: 22, y: 73 }] },
  // WR1 out: DB/LB1 matches WR1 to the flat
  { routeTrigger: 'out', response: [{ x: 28, y: 65 }, { x: 22, y: 68 }, { x: 8, y: 68 }] },
  { routeTrigger: 'out_wr1', response: [{ x: 28, y: 65 }, { x: 22, y: 68 }, { x: 8, y: 68 }] },
  // WR2 vertical: wall briefly then settle (FS takes vertical)
  { routeTrigger: 'go', response: [{ x: 32, y: 62 }, { x: 32, y: 55 }, { x: 30, y: 60 }] },
  { routeTrigger: 'deepOut', response: [{ x: 32, y: 62 }, { x: 32, y: 55 }, { x: 28, y: 60 }] },
  { routeTrigger: 'deepIn', response: [{ x: 32, y: 62 }, { x: 32, y: 55 }, { x: 30, y: 60 }] },
  { routeTrigger: 'comeback', response: [{ x: 32, y: 62 }, { x: 32, y: 55 }, { x: 28, y: 60 }] },
  { routeTrigger: 'post', response: [{ x: 32, y: 62 }, { x: 32, y: 55 }, { x: 30, y: 60 }] },
  { routeTrigger: 'corner', response: [{ x: 32, y: 62 }, { x: 32, y: 55 }, { x: 28, y: 60 }] },
  { routeTrigger: 'curl', response: [{ x: 32, y: 62 }, { x: 32, y: 52 }, { x: 32, y: 60 }] },
  // Push call: RB out fast
  { routeTrigger: 'out_rb', response: [{ x: 30, y: 65 }, { x: 40, y: 72 }, { x: 55, y: 77 }] },
  { routeTrigger: 'swing_rb', response: [{ x: 30, y: 65 }, { x: 45, y: 75 }, { x: 70, y: 80 }] },
  { routeTrigger: 'wheel_rb', response: [{ x: 30, y: 65 }, { x: 45, y: 75 }, { x: 65, y: 78 }, { x: 65, y: 12 }] },
  // WR3 short out on strong side
  { routeTrigger: 'out_wr3', response: [{ x: 32, y: 65 }, { x: 38, y: 70 }, { x: 30, y: 73 }, { x: 25, y: 75 }] },
];

// --- FS (3 WR side Safety): Play Robber-Read ---
const tripsCover6FSReactions: FSReaction[] = [
  // bracket_* reactions: rob WR1 from over the top
  { routeTrigger: 'bracket_slant', response: [{ x: 28, y: 38 }, { x: 35, y: 30 }, { x: 50, y: 22 }, { x: 65, y: 18 }] },
  { routeTrigger: 'bracket_out', response: [{ x: 28, y: 38 }, { x: 22, y: 32 }, { x: 15, y: 28 }, { x: 10, y: 25 }] },
  { routeTrigger: 'bracket_deepOut', response: [{ x: 28, y: 35 }, { x: 22, y: 25 }, { x: 12, y: 20 }, { x: 8, y: 18 }] },
  { routeTrigger: 'bracket_deepIn', response: [{ x: 28, y: 35 }, { x: 25, y: 25 }, { x: 40, y: 20 }, { x: 55, y: 18 }] },
  { routeTrigger: 'bracket_comeback', response: [{ x: 28, y: 35 }, { x: 22, y: 20 }, { x: 18, y: 12 }, { x: 17, y: 18 }] },
  { routeTrigger: 'bracket_go', response: [{ x: 25, y: 35 }, { x: 22, y: 22 }, { x: 22, y: 12 }, { x: 22, y: 5 }] },
  { routeTrigger: 'bracket_post', response: [{ x: 28, y: 35 }, { x: 25, y: 28 }, { x: 35, y: 18 }, { x: 48, y: 10 }] },
  { routeTrigger: 'bracket_corner', response: [{ x: 28, y: 35 }, { x: 22, y: 28 }, { x: 12, y: 18 }, { x: 8, y: 10 }] },
  { routeTrigger: 'bracket_drag', response: [{ x: 30, y: 40 }, { x: 30, y: 35 }, { x: 40, y: 32 }, { x: 55, y: 30 }] },
  { routeTrigger: 'bracket_hitch', response: [{ x: 28, y: 38 }, { x: 24, y: 32 }, { x: 22, y: 28 }, { x: 22, y: 25 }] },
  { routeTrigger: 'bracket_curl', response: [{ x: 28, y: 35 }, { x: 24, y: 28 }, { x: 22, y: 22 }, { x: 24, y: 25 }] },
  // WR2 out early: FS takes WR1 man
  { routeTrigger: 'out_wr2_slant', response: [{ x: 28, y: 38 }, { x: 30, y: 30 }, { x: 50, y: 22 }, { x: 65, y: 18 }] },
  { routeTrigger: 'out_wr2_go', response: [{ x: 25, y: 35 }, { x: 22, y: 22 }, { x: 22, y: 12 }, { x: 22, y: 5 }] },
  { routeTrigger: 'out_wr2_post', response: [{ x: 28, y: 35 }, { x: 25, y: 28 }, { x: 35, y: 18 }, { x: 48, y: 10 }] },
  { routeTrigger: 'out_wr2_corner', response: [{ x: 28, y: 35 }, { x: 22, y: 28 }, { x: 12, y: 18 }, { x: 8, y: 10 }] },
  // Default short route fallbacks
  { routeTrigger: 'slant', response: [{ x: 30, y: 38 }, { x: 28, y: 30 }, { x: 30, y: 22 }, { x: 35, y: 18 }] },
  { routeTrigger: 'out', response: [{ x: 30, y: 38 }, { x: 25, y: 30 }, { x: 20, y: 22 }, { x: 18, y: 18 }] },
  // WR2 vertical: take WR2 man
  { routeTrigger: 'deepOut', response: [{ x: 35, y: 35 }, { x: 32, y: 42 }, { x: 20, y: 45 }] },
  { routeTrigger: 'deepIn', response: [{ x: 35, y: 35 }, { x: 35, y: 42 }, { x: 58, y: 42 }] },
  { routeTrigger: 'comeback', response: [{ x: 35, y: 35 }, { x: 32, y: 28 }, { x: 28, y: 40 }] },
  { routeTrigger: 'go', response: [{ x: 35, y: 35 }, { x: 35, y: 20 }, { x: 35, y: 10 }] },
  { routeTrigger: 'post', response: [{ x: 35, y: 38 }, { x: 38, y: 30 }, { x: 52, y: 15 }] },
  { routeTrigger: 'corner', response: [{ x: 35, y: 38 }, { x: 32, y: 30 }, { x: 15, y: 15 }] },
  { routeTrigger: 'drag', response: [{ x: 30, y: 38 }, { x: 28, y: 30 }, { x: 25, y: 22 }, { x: 22, y: 18 }] },
  { routeTrigger: 'hitch', response: [{ x: 30, y: 38 }, { x: 25, y: 30 }, { x: 22, y: 22 }, { x: 20, y: 18 }] },
  { routeTrigger: 'curl', response: [{ x: 35, y: 38 }, { x: 33, y: 30 }, { x: 32, y: 22 }, { x: 32, y: 18 }] },
];

// --- CB2 (Single WR side Corner): Man on WR4 everywhere ---
// Uses manCB2Reactions (straight man, no exceptions)

// --- DB/LB2 (Single WR side Slot): Zone, picks up crossers from trips side ---
const tripsCover6DBLB2Reactions: DBLB2Reaction[] = [
  // Default: zone drop, read for crossers from trips side
  { routeTrigger: 'slant', response: [{ x: 70, y: 65 }, { x: 65, y: 55 }, { x: 58, y: 48 }] },
  { routeTrigger: 'out', response: [{ x: 70, y: 65 }, { x: 68, y: 55 }, { x: 65, y: 48 }] },
  { routeTrigger: 'deepOut', response: [{ x: 70, y: 65 }, { x: 68, y: 55 }, { x: 65, y: 48 }] },
  { routeTrigger: 'deepIn', response: [{ x: 70, y: 65 }, { x: 65, y: 55 }, { x: 58, y: 48 }] },
  { routeTrigger: 'comeback', response: [{ x: 70, y: 65 }, { x: 68, y: 55 }, { x: 65, y: 48 }] },
  { routeTrigger: 'go', response: [{ x: 70, y: 65 }, { x: 68, y: 55 }, { x: 65, y: 48 }] },
  { routeTrigger: 'post', response: [{ x: 70, y: 65 }, { x: 65, y: 55 }, { x: 58, y: 48 }] },
  { routeTrigger: 'corner', response: [{ x: 70, y: 65 }, { x: 68, y: 55 }, { x: 65, y: 48 }] },
  { routeTrigger: 'drag', response: [{ x: 70, y: 65 }, { x: 65, y: 55 }, { x: 58, y: 48 }] },
  { routeTrigger: 'hitch', response: [{ x: 70, y: 65 }, { x: 68, y: 55 }, { x: 65, y: 48 }] },
  { routeTrigger: 'curl', response: [{ x: 70, y: 65 }, { x: 68, y: 55 }, { x: 65, y: 48 }] },
  { routeTrigger: 'zone_drop', response: [{ x: 70, y: 65 }, { x: 68, y: 55 }, { x: 65, y: 48 }] },
  // Pick up crossers from trips side
  { routeTrigger: 'slant_wr1', response: [{ x: 70, y: 65 }, { x: 62, y: 55 }, { x: 65, y: 45 }, { x: 72, y: 40 }] },
  { routeTrigger: 'slant_wr2', response: [{ x: 70, y: 65 }, { x: 62, y: 55 }, { x: 68, y: 46 }, { x: 80, y: 42 }] },
  { routeTrigger: 'slant_wr3', response: [{ x: 70, y: 65 }, { x: 62, y: 55 }, { x: 70, y: 46 }, { x: 85, y: 42 }] },
  { routeTrigger: 'slant_wr4', response: [{ x: 70, y: 65 }, { x: 60, y: 55 }, { x: 45, y: 42 }] },
  { routeTrigger: 'drag_wr1', response: [{ x: 70, y: 65 }, { x: 62, y: 68 }, { x: 68, y: 72 }, { x: 75, y: 75 }] },
  { routeTrigger: 'drag_wr2', response: [{ x: 70, y: 65 }, { x: 62, y: 68 }, { x: 72, y: 74 }, { x: 80, y: 78 }] },
  { routeTrigger: 'drag_wr3', response: [{ x: 70, y: 65 }, { x: 62, y: 68 }, { x: 75, y: 76 }, { x: 88, y: 80 }] },
  { routeTrigger: 'drag_wr4', response: [{ x: 70, y: 65 }, { x: 62, y: 70 }, { x: 48, y: 74 }, { x: 35, y: 75 }] },
  { routeTrigger: 'out_wr4', response: [{ x: 72, y: 65 }, { x: 78, y: 68 }, { x: 92, y: 68 }] },
  { routeTrigger: 'hitch_wr4', response: [{ x: 72, y: 65 }, { x: 76, y: 68 }, { x: 78, y: 73 }] },
];

// --- SS (Single WR side Safety): Deep half, no match responsibilities ---
const tripsCover6SSReactions: SSReaction[] = [
  // Deep half — stays deep on all routes, no matching
  { routeTrigger: 'slant', response: [{ x: 75, y: 30 }, { x: 75, y: 20 }] },
  { routeTrigger: 'out', response: [{ x: 75, y: 30 }, { x: 75, y: 20 }] },
  { routeTrigger: 'deepOut', response: [{ x: 78, y: 28 }, { x: 85, y: 18 }] },
  { routeTrigger: 'deepIn', response: [{ x: 72, y: 28 }, { x: 60, y: 18 }] },
  { routeTrigger: 'comeback', response: [{ x: 75, y: 28 }, { x: 78, y: 18 }] },
  { routeTrigger: 'go', response: [{ x: 75, y: 25 }, { x: 78, y: 10 }] },
  { routeTrigger: 'post', response: [{ x: 72, y: 28 }, { x: 60, y: 15 }] },
  { routeTrigger: 'corner', response: [{ x: 78, y: 28 }, { x: 90, y: 12 }] },
  { routeTrigger: 'drag', response: [{ x: 75, y: 30 }, { x: 75, y: 20 }] },
  { routeTrigger: 'hitch', response: [{ x: 75, y: 30 }, { x: 75, y: 20 }] },
  { routeTrigger: 'curl', response: [{ x: 75, y: 28 }, { x: 75, y: 18 }] },
];

// --- LB (Hook): Hammer on #3, "Push" call if RB out fast ---
const tripsCover6LBReactions: LBReaction[] = [
  // RB out fast → "Push" call → LB covers #2 (WR2) with underneath leverage
  // WR2 starts at (30, 90), so LB shifts to strong side to wall WR2
  { routeTrigger: 'out', response: [{ x: 48, y: 68 }, { x: 42, y: 62 }, { x: 35, y: 58 }, { x: 32, y: 55 }] },
  { routeTrigger: 'swing', response: [{ x: 48, y: 68 }, { x: 42, y: 62 }, { x: 35, y: 58 }, { x: 32, y: 55 }] },
  { routeTrigger: 'wheel', response: [{ x: 48, y: 68 }, { x: 42, y: 62 }, { x: 35, y: 58 }, { x: 32, y: 55 }] },
  // RB curl / stays in → man on #3
  { routeTrigger: 'curl', response: [{ x: 52, y: 75 }, { x: 53, y: 80 }, { x: 52, y: 80 }] },
  // Default: man on RB shadowing in backfield
  { routeTrigger: 'man_rb', response: [{ x: 53, y: 75 }, { x: 55, y: 80 }, { x: 55, y: 82 }] },
  // Push call alternative key (for AnimationEngine compatibility)
  { routeTrigger: 'push_wr2', response: [{ x: 48, y: 68 }, { x: 42, y: 62 }, { x: 35, y: 58 }, { x: 32, y: 55 }] },
  { routeTrigger: 'zone_drop', response: [{ x: 50, y: 66 }, { x: 50, y: 60 }, { x: 50, y: 52 }] },
  // Crosser triggers
  { routeTrigger: 'slant_wr1', response: [{ x: 48, y: 64 }, { x: 42, y: 52 }, { x: 52, y: 44 }, { x: 64, y: 42 }, { x: 75, y: 40 }] },
  { routeTrigger: 'slant_wr2', response: [{ x: 48, y: 64 }, { x: 42, y: 52 }, { x: 55, y: 46 }, { x: 70, y: 44 }, { x: 83, y: 43 }] },
  { routeTrigger: 'slant_wr3', response: [{ x: 50, y: 64 }, { x: 52, y: 55 }, { x: 65, y: 48 }, { x: 80, y: 44 }, { x: 93, y: 43 }] },
  { routeTrigger: 'slant_wr4', response: [{ x: 52, y: 64 }, { x: 58, y: 52 }, { x: 48, y: 44 }, { x: 36, y: 42 }, { x: 25, y: 40 }] },
  { routeTrigger: 'drag_wr1', response: [{ x: 48, y: 68 }, { x: 45, y: 72 }, { x: 55, y: 74 }, { x: 68, y: 75 }] },
  { routeTrigger: 'drag_wr2', response: [{ x: 48, y: 68 }, { x: 45, y: 74 }, { x: 60, y: 78 }, { x: 78, y: 80 }] },
  { routeTrigger: 'drag_wr3', response: [{ x: 50, y: 68 }, { x: 52, y: 74 }, { x: 70, y: 78 }, { x: 88, y: 80 }] },
  { routeTrigger: 'drag_wr4', response: [{ x: 52, y: 68 }, { x: 55, y: 74 }, { x: 40, y: 74 }, { x: 32, y: 75 }] },
  { routeTrigger: 'slant_wr3_handoff', response: [{ x: 50, y: 64 }, { x: 52, y: 55 }, { x: 65, y: 48 }, { x: 80, y: 44 }] },
  { routeTrigger: 'slant_wr4_handoff', response: [{ x: 52, y: 64 }, { x: 58, y: 52 }, { x: 48, y: 44 }, { x: 36, y: 42 }] },
  { routeTrigger: 'drag_wr3_handoff', response: [{ x: 50, y: 68 }, { x: 52, y: 74 }, { x: 70, y: 78 }] },
  { routeTrigger: 'drag_wr4_handoff', response: [{ x: 52, y: 68 }, { x: 55, y: 74 }, { x: 40, y: 74 }] },
];

const tripsCover6Definition: CoverageDefinition = {
  id: 'cover6',
  name: 'Cover 6',
  description: 'Cover 6 vs 3x1 — trips side plays Cover 4 Palms, single WR side plays simplified man/deep half',
  initialPosition: { x: 20, y: 65 },
  reactionRules: tripsCover6CB1Reactions,
  dblb1InitialPosition: { x: 30, y: 72 },
  dblb1ReactionRules: tripsCover6DBLB1Reactions,
  fsInitialPosition: { x: 35, y: 50 },
  fsReactionRules: tripsCover6FSReactions,
  cb2InitialPosition: { x: 80, y: 65 },
  cb2ReactionRules: manCB2Reactions,
  dblb2InitialPosition: { x: 70, y: 72 },
  dblb2ReactionRules: tripsCover6DBLB2Reactions,
  ssInitialPosition: { x: 75, y: 35 },
  ssReactionRules: tripsCover6SSReactions,
  lbInitialPosition: { x: 50, y: 70 },
  lbReactionRules: tripsCover6LBReactions,
};

const tripsCoverageDefinitions: Record<string, CoverageDefinition> = {
  man: tripsManDefinition,
  cover2: cover2Definition,
  cover3: cover3Definition,
  cover4Match: cover4MatchDefinition,
  cover4Palms: tripsCover4PalmsDefinition,
  cover6: tripsCover6Definition,
  cover3Match: tripsCover3MatchDefinition,
};

/**
 * Top-level registry keyed by formation.
 */
const formationCoverageRegistry: Record<FormationType, Record<string, CoverageDefinition>> = {
  doubles: doublesCoverageDefinitions,
  trips: tripsCoverageDefinitions,
};

/**
 * Legacy flat alias — kept for backward compatibility with add/update/remove helpers.
 * Points at the doubles registry.
 */
const coverageDefinitions = doublesCoverageDefinitions;

// ============================================
// Internal helper
// ============================================

function resolveDefinition(coverageType: string, formation: FormationType = 'doubles'): CoverageDefinition | undefined {
  const registry = formationCoverageRegistry[formation] ?? doublesCoverageDefinitions;
  return registry[coverageType];
}

// ============================================
// Public API — all functions accept an optional formation param
// ============================================

/**
 * Get all available coverage types for a formation
 */
export function getAvailableCoverages(formation: FormationType = 'doubles'): string[] {
  const registry = formationCoverageRegistry[formation] ?? doublesCoverageDefinitions;
  return Object.keys(registry);
}

/**
 * Get coverage definition by type and formation
 */
export function getCoverageDefinition(coverageType: string, formation: FormationType = 'doubles'): CoverageDefinition | undefined {
  return resolveDefinition(coverageType, formation);
}

/**
 * Get the initial CB1 position for a coverage type
 */
export function getCB1StartPosition(coverageType: string, formation: FormationType = 'doubles'): Position {
  const coverage = resolveDefinition(coverageType, formation);
  if (coverage) {
    return { ...coverage.initialPosition };
  }
  return getCover4MatchStartPosition();
}

/**
 * Get the initial DB/LB1 position for a coverage type
 */
export function getDBLB1StartPosition(coverageType: string, formation: FormationType = 'doubles'): Position {
  const coverage = resolveDefinition(coverageType, formation);
  if (coverage && coverage.dblb1InitialPosition) {
    return { ...coverage.dblb1InitialPosition };
  }
  return getCover4MatchDBLB1StartPosition();
}

/**
 * Get the initial FS position for a coverage type
 */
export function getFSStartPosition(coverageType: string, formation: FormationType = 'doubles'): Position {
  const coverage = resolveDefinition(coverageType, formation);
  if (coverage && coverage.fsInitialPosition) {
    return { ...coverage.fsInitialPosition };
  }
  return getCover4MatchFSStartPosition();
}

/**
 * Get the initial CB2 position for a coverage type
 */
export function getCB2StartPosition(coverageType: string, formation: FormationType = 'doubles'): Position {
  const coverage = resolveDefinition(coverageType, formation);
  if (coverage && coverage.cb2InitialPosition) {
    return { ...coverage.cb2InitialPosition };
  }
  return { x: 80, y: 65 };
}

/**
 * Get the initial DB/LB2 position for a coverage type
 */
export function getDBLB2StartPosition(coverageType: string, formation: FormationType = 'doubles'): Position {
  const coverage = resolveDefinition(coverageType, formation);
  if (coverage && coverage.dblb2InitialPosition) {
    return { ...coverage.dblb2InitialPosition };
  }
  return { x: 70, y: 72 };
}

/**
 * Get the initial SS position for a coverage type
 */
export function getSSStartPosition(coverageType: string, formation: FormationType = 'doubles'): Position {
  const coverage = resolveDefinition(coverageType, formation);
  if (coverage && coverage.ssInitialPosition) {
    return { ...coverage.ssInitialPosition };
  }
  return { x: 65, y: 50 };
}

/**
 * Get the initial LB position for a coverage type
 */
export function getLBStartPosition(coverageType: string, formation: FormationType = 'doubles'): Position {
  const coverage = resolveDefinition(coverageType, formation);
  if (coverage && coverage.lbInitialPosition) {
    return { ...coverage.lbInitialPosition };
  }
  return { x: 50, y: 70 };
}

/**
 * Get reaction for a specific route and coverage combination
 */
export function getCoverageReaction(coverageType: string, routeTrigger: string, formation: FormationType = 'doubles'): CoverageReaction | undefined {
  const coverage = resolveDefinition(coverageType, formation);
  if (!coverage) return undefined;
  return coverage.reactionRules.find(r => r.routeTrigger === routeTrigger);
}

/**
 * Get DB/LB1 reaction for a specific route and coverage combination
 */
export function getDBLB1Reaction(coverageType: string, routeTrigger: string, formation: FormationType = 'doubles'): DBLBReaction | undefined {
  const coverage = resolveDefinition(coverageType, formation);
  if (!coverage || !coverage.dblb1ReactionRules) return undefined;
  return coverage.dblb1ReactionRules.find(r => r.routeTrigger === routeTrigger);
}

/**
 * Get FS reaction for a specific route and coverage combination
 */
export function getFSReaction(coverageType: string, routeTrigger: string, formation: FormationType = 'doubles'): FSReaction | undefined {
  const coverage = resolveDefinition(coverageType, formation);
  if (!coverage || !coverage.fsReactionRules) return undefined;
  return coverage.fsReactionRules.find(r => r.routeTrigger === routeTrigger);
}

/**
 * Get CB2 reaction for a specific route and coverage combination
 */
export function getCB2Reaction(coverageType: string, routeTrigger: string, formation: FormationType = 'doubles'): CB2Reaction | undefined {
  const coverage = resolveDefinition(coverageType, formation);
  if (!coverage || !coverage.cb2ReactionRules) return undefined;
  return coverage.cb2ReactionRules.find(r => r.routeTrigger === routeTrigger);
}

/**
 * Get DB/LB2 reaction for a specific route and coverage combination
 */
export function getDBLB2Reaction(coverageType: string, routeTrigger: string, formation: FormationType = 'doubles'): DBLB2Reaction | undefined {
  const coverage = resolveDefinition(coverageType, formation);
  if (!coverage || !coverage.dblb2ReactionRules) return undefined;
  return coverage.dblb2ReactionRules.find(r => r.routeTrigger === routeTrigger);
}

/**
 * Get SS reaction for a specific route and coverage combination
 */
export function getSSReaction(coverageType: string, routeTrigger: string, formation: FormationType = 'doubles'): SSReaction | undefined {
  const coverage = resolveDefinition(coverageType, formation);
  if (!coverage || !coverage.ssReactionRules) return undefined;
  return coverage.ssReactionRules.find(r => r.routeTrigger === routeTrigger);
}

/**
 * Get LB reaction for a specific RB route and coverage combination
 */
export function getLBReaction(coverageType: string, routeTrigger: string, formation: FormationType = 'doubles'): LBReaction | undefined {
  const coverage = resolveDefinition(coverageType, formation);
  if (!coverage || !coverage.lbReactionRules) return undefined;
  return coverage.lbReactionRules.find(r => r.routeTrigger === routeTrigger);
}

/**
 * Check if a coverage type is valid (exists in the registry)
 */
export function isValidCoverage(coverageType: string, formation: FormationType = 'doubles'): boolean {
  const registry = formationCoverageRegistry[formation] ?? doublesCoverageDefinitions;
  return coverageType in registry;
}

// ============================================
// Validation & mutation helpers (operate on doubles registry for backward compat)
// ============================================

/**
 * Validates a coverage definition has all required fields
 */
export function validateCoverageDefinition(coverage: CoverageDefinition): boolean {
  if (!coverage.id || typeof coverage.id !== 'string' || coverage.id.trim() === '') return false;
  if (!coverage.name || typeof coverage.name !== 'string') return false;
  if (!coverage.description || typeof coverage.description !== 'string') return false;
  if (!coverage.initialPosition ||
      typeof coverage.initialPosition.x !== 'number' ||
      typeof coverage.initialPosition.y !== 'number') return false;
  if (!Array.isArray(coverage.reactionRules)) return false;

  for (const reaction of coverage.reactionRules) {
    if (!reaction.routeTrigger || typeof reaction.routeTrigger !== 'string') return false;
    if (!reaction.wrPositionTrigger ||
        typeof reaction.wrPositionTrigger.x !== 'number' ||
        typeof reaction.wrPositionTrigger.y !== 'number') return false;
    if (!Array.isArray(reaction.cbResponse) || reaction.cbResponse.length === 0) return false;
    if (typeof reaction.responseDelay !== 'number' || reaction.responseDelay < 0) return false;
    for (const pos of reaction.cbResponse) {
      if (typeof pos.x !== 'number' || typeof pos.y !== 'number') return false;
    }
  }
  return true;
}

/**
 * Add a new coverage to a formation's registry (defaults to doubles)
 */
export function addCoverage(coverage: CoverageDefinition, formation: FormationType = 'doubles'): boolean {
  if (!validateCoverageDefinition(coverage)) {
    console.error(`Invalid coverage definition: ${coverage.id}`);
    return false;
  }
  const registry = formationCoverageRegistry[formation] ?? doublesCoverageDefinitions;
  if (registry[coverage.id]) {
    console.warn(`Coverage '${coverage.id}' already exists in ${formation}. Use updateCoverage to modify.`);
    return false;
  }
  registry[coverage.id] = coverage;
  return true;
}

/**
 * Update an existing coverage in a formation's registry
 */
export function updateCoverage(coverage: CoverageDefinition, formation: FormationType = 'doubles'): boolean {
  if (!validateCoverageDefinition(coverage)) {
    console.error(`Invalid coverage definition: ${coverage.id}`);
    return false;
  }
  const registry = formationCoverageRegistry[formation] ?? doublesCoverageDefinitions;
  if (!registry[coverage.id]) {
    console.warn(`Coverage '${coverage.id}' does not exist in ${formation}. Use addCoverage to create.`);
    return false;
  }
  registry[coverage.id] = coverage;
  return true;
}

/**
 * Remove a coverage from a formation's registry
 */
export function removeCoverage(coverageId: string, formation: FormationType = 'doubles'): boolean {
  const builtInCoverages = ['cover4Match', 'man'];
  if (builtInCoverages.includes(coverageId)) {
    console.warn(`Cannot remove built-in coverage: ${coverageId}`);
    return false;
  }
  const registry = formationCoverageRegistry[formation] ?? doublesCoverageDefinitions;
  if (!registry[coverageId]) {
    console.warn(`Coverage '${coverageId}' does not exist in ${formation}.`);
    return false;
  }
  delete registry[coverageId];
  return true;
}

/**
 * Get the count of registered coverages for a formation
 */
export function getCoverageCount(formation: FormationType = 'doubles'): number {
  const registry = formationCoverageRegistry[formation] ?? doublesCoverageDefinitions;
  return Object.keys(registry).length;
}

/**
 * Add a reaction rule to an existing coverage
 */
export function addCoverageReaction(coverageId: string, reaction: CoverageReaction, formation: FormationType = 'doubles'): boolean {
  const registry = formationCoverageRegistry[formation] ?? doublesCoverageDefinitions;
  const coverage = registry[coverageId];
  if (!coverage) {
    console.warn(`Coverage '${coverageId}' does not exist in ${formation}.`);
    return false;
  }
  if (!reaction.routeTrigger || typeof reaction.routeTrigger !== 'string') return false;
  if (!reaction.wrPositionTrigger ||
      typeof reaction.wrPositionTrigger.x !== 'number' ||
      typeof reaction.wrPositionTrigger.y !== 'number') return false;
  if (!Array.isArray(reaction.cbResponse) || reaction.cbResponse.length === 0) return false;
  if (typeof reaction.responseDelay !== 'number' || reaction.responseDelay < 0) return false;

  const existingIndex = coverage.reactionRules.findIndex(r => r.routeTrigger === reaction.routeTrigger);
  if (existingIndex >= 0) {
    coverage.reactionRules[existingIndex] = reaction;
  } else {
    coverage.reactionRules.push(reaction);
  }
  return true;
}

export { coverageDefinitions, formationCoverageRegistry };
